import * as Sentry from '@sentry/react';
import Axios, { AxiosRequestConfig, AxiosResponseHeaders, RawAxiosResponseHeaders } from 'axios';
import { REQUEST_API_URL_WITHOUT_API_V1 } from '../../../constants/common';
import { getToken } from '../../../utils/common/cookies';

/**
 * カスタムAxiosインスタンス
 * @remarks
 * - baseURL: REQUEST_API_URL_WITHOUT_API_V1（/api/v1を除いたベースURL）
 * - timeout: 15秒（環境に応じて調整可能）
 */
const axiosInstance = Axios.create({
  baseURL: REQUEST_API_URL_WITHOUT_API_V1,
  timeout: 15000, // 15s: adjust as needed per environment
  // Axiosデフォルトの 'application/json, text/plain, */*' だと
  // Rails が BROWSER_LIKE_ACCEPTS (/,\s*\*\/\*|\*\/\*\s*,/) にマッチし
  // Accept ヘッダーを無視して HTML フォーマットにフォールバックするため
  // respond_to ブロックに format.html がないエンドポイントで 406 になる
  headers: {
    Accept: 'application/json',
  },
});

/**
 * リクエストインターセプター（認証）
 * @description
 * - Authorizationヘッダーにトークンを自動付与
 * - 非GETリクエストにContent-Type: application/jsonを設定
 * @param config - Axiosリクエスト設定
 */
axiosInstance.interceptors.request.use(
  config => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // FormData の場合は Content-Type を設定しない。
    // Axios が multipart/form-data; boundary=... を自動付与するため、
    // ここで application/json を上書きすると boundary が失われてサーバーがパースできなくなる。
    if (
      config.method?.toUpperCase() !== 'GET' &&
      !config.headers?.['Content-Type'] &&
      !(config.data instanceof FormData)
    ) {
      config.headers['Content-Type'] = 'application/json';
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

/**
 * 404 レスポンスを Sentry に送るべき「バグ由来の疑いが強いリクエスト」か判定する。
 *
 * 背景:
 * - 通常の 404（削除済みリソースへのアクセス・ルート抜けなど）はアクションに繋がらないノイズ
 * - 一方、ID interpolation バグ（例: `/api/v1/DEF/partners/undefined`）は本番で検知したい
 *
 * 判定条件: URL のパスセグメントまたはクエリパラメータ値に undefined / null / NaN が含まれる場合
 * - パスセグメント: `.../undefined/...` / `.../null` / `.../NaN?...` などにマッチ
 * - クエリパラメータ値: `?foo=undefined` / `&bar=null` / `&baz=NaN` などにマッチ
 */
const isSuspiciousNotFoundUrl = (url: string): boolean => {
  // パスセグメントとして undefined/null/NaN が現れるパターン（末尾・スラッシュ・クエリ区切りのいずれかで終端）
  const pathPattern = /\/(undefined|null|NaN)(\/|\?|$)/;
  // クエリパラメータ値として undefined/null/NaN が現れるパターン
  const queryPattern = /[?&][^=]+=(?:undefined|null|NaN)(&|$)/;
  return pathPattern.test(url) || queryPattern.test(url);
};

/**
 * レスポンスインターセプター（Sentry連携）
 * 既存のresponseMiddlewareと同じロジック
 */
axiosInstance.interceptors.response.use(
  response => {
    // 401ハンドリング（既存のロジック）
    if (response.status === 401) {
      // 既存では特に処理なし
    }

    return response;
  },
  error => {
    // ネットワークエラーやエラーレスポンスの場合
    if (error.response) {
      const { status } = error.response;

      // 401 はトークン切れ・Cookie 失効による未認証状態の正常系ノイズのため Sentry に送らない
      // 404 は削除済みリソースへのアクセス等のノイズが多いため、URL に undefined/null/NaN が
      // 混入しているバグ由来と疑われるリクエストのみ capture する
      const shouldCapture =
        status !== 401 && (status !== 404 || isSuspiciousNotFoundUrl(error.config?.url ?? ''));

      if (shouldCapture) {
        // エラーレスポンスがある場合
        const errorData = error.response.data;

        // errorDataをパースしてSentryに送信
        const errorMessage =
          typeof errorData?.errors === 'string'
            ? errorData.errors
            : JSON.stringify(errorData?.errors || 'An unknown error occurred');

        Sentry.captureException(new Error(errorMessage), {
          tags: {
            api_url: error.config?.url,
            status_code: status,
            method: error.config?.method,
          },
          extra: {
            response_body: errorData,
            request_url: error.config?.url,
          },
        });
      }
    } else {
      // ネットワークエラー等（レスポンスがない場合）
      // 既存ロジックのcatch部分と同等
      Sentry.captureException(new Error('Network error or no response'), {
        tags: {
          api_url: error.config?.url,
          method: error.config?.method,
        },
      });
    }

    return Promise.reject(error);
  }
);

/**
 * Orvalが使用するカスタムインスタンス関数
 * この関数がOrvalの生成コードから呼び出される
 * @template T - レスポンスデータの型
 * @param url - リクエストURL（Orval 8.x 以降の新シグネチャ）
 * @param init - RequestInit オブジェクト（method / headers / body / signal 等）
 * @returns キャンセル可能なPromise（cancelメソッド付き）
 *
 * @remarks
 * Orval 7.x → 8.x で mutator の呼び出し規約が変更された。
 * 8.x では `customInstance(url, { method, headers, body, ...options })` の形で呼ばれる。
 *
 * この関数はレスポンスボディ（data）のみを返す。
 * ページネーション情報（total-pages, total-count 等）はレスポンスヘッダーに含まれるため、
 * ヘッダー情報が必要な場合はOrval生成のfetcherは使用できない。
 * その場合はkey生成関数を流用しつつfetcherを自作すること。
 */
type CancelablePromise<T> = Promise<T> & { cancel: () => void };

export const customInstance = <T>(url: string, init?: RequestInit): CancelablePromise<T> => {
  const controller = new AbortController();
  const { method, headers, body, signal, ...rest } = init ?? {};

  const promise = axiosInstance({
    url,
    method: method as AxiosRequestConfig['method'],
    headers: headers as AxiosRequestConfig['headers'],
    // RequestInit の body は文字列（JSON.stringify済み）なので data として渡す
    data: body,
    signal: (signal as AbortSignal | null | undefined) ?? controller.signal,
    ...rest,
  }).then(({ data }) => data) as CancelablePromise<T>;

  promise.cancel = () => {
    controller.abort();
  };

  return promise;
};

/**
 * CSV（Blob）ダウンロード用カスタムインスタンス関数
 * @param config - Axiosリクエスト設定
 * @returns Blobデータ
 *
 * @remarks
 * - responseType: 'blob' でバイナリデータとして受け取る
 * - Accept: 'text/csv' でインスタンスデフォルトの 'application/json' を上書き
 * - Cache-Control: 'no-cache' を付与することで304レスポンスを回避する
 */
export const customInstanceForCsv = (config: AxiosRequestConfig): Promise<Blob> => {
  return axiosInstance({
    ...config,
    responseType: 'blob',
    headers: {
      ...config.headers,
      Accept: 'text/csv',
      'Cache-Control': 'no-cache',
    },
  }).then(({ data }) => data);
};

/**
 * ヘッダー付きレスポンスの型
 * @template T - レスポンスデータの型
 */
export type ResponseWithHeaders<T> = {
  data: T;
  headers: RawAxiosResponseHeaders | AxiosResponseHeaders;
};

/**
 * レスポンスヘッダーも返すカスタムインスタンス関数
 * @template T - レスポンスデータの型
 * @param url - リクエストURL（Orval 8.x 以降の新シグネチャ）
 * @param init - RequestInit オブジェクト（method / headers / body / signal 等）
 * @returns キャンセル可能なPromise（data + headers）
 *
 * @remarks
 * ページネーション情報（total-pages, total-count 等）など
 * レスポンスヘッダーの値が必要な場合にこちらを使用する。
 */
export const customInstanceWithHeader = <T>(
  url: string,
  init?: RequestInit
): CancelablePromise<ResponseWithHeaders<T>> => {
  const controller = new AbortController();
  const { method, headers, body, signal, ...rest } = init ?? {};

  const promise = axiosInstance({
    url,
    method: method as AxiosRequestConfig['method'],
    headers: headers as AxiosRequestConfig['headers'],
    data: body,
    signal: (signal as AbortSignal | null | undefined) ?? controller.signal,
    ...rest,
  }).then(({ data, headers }) => ({
    data,
    headers,
  })) as CancelablePromise<ResponseWithHeaders<T>>;

  promise.cancel = () => {
    controller.abort();
  };

  return promise;
};
